// @flow
import React from 'react'
import PropTypes from 'prop-types'
import T from 'i18n-react'
import classNames from 'classnames'

// libs
import { validate } from 'devx-js-utilities'

export default class TextInput extends React.Component {
  static propTypes = {
    canBeEmpty: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    inputType: PropTypes.string,
    isInteger: PropTypes.bool,
    isNumber: PropTypes.bool,
    isPhone: PropTypes.bool,
    isRequired: PropTypes.bool,
    label: PropTypes.string.isRequired,
    labelNote: PropTypes.string,
    maxLength: PropTypes.number,
    min: PropTypes.number,
    minLength: PropTypes.number,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onErrorChange: PropTypes.func,
    range: PropTypes.object,
    regExp: PropTypes.any,
    required: PropTypes.bool,
    tooltip: PropTypes.node,
    validationErrors: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    wrapperClassName: PropTypes.string,
  }

  static defaultProps = {
    canBeEmpty: false,
    className: 'form-control',
    disabled: false,
    errorStyle: {},
    floatingLabelFixed: false,
    floatingLabelFocusStyle: null,
    floatingLabelShrinkStyle: null,
    floatingLabelStyle: null,
    floatingLabelText: null,
    isInteger: false,
    isNumber: false,
    isPhone: false,
    isRequired: false,
    label: 'Input label',
    labelNote: '',
    maxLength: Number.MAX_SAFE_INTEGER,
    min: null,
    minLength: 0,
    onErrorChange: () => null,
    range: { start: null, end: null },
    regExp: null,
    required: true,
    tooltip: null,
    inputType: 'text',
    validationErrors: {},
    wrapperClassName: '',
  }

  isValid = () => this.validate(true, this.props.value)

  validate = (showError = true, value = this.props.value) => {
    if (this.props.canBeEmpty && value.length === 0) {
      // just for no validating empty field
    } else if (this.props.isRequired && value.length <= 0) {
      showError && this.props.onErrorChange(this.props.name, T.translate('cl.validationErrors.required'))
      return false
    } else if (this.props.regExp && !this.props.regExp.test(value)) {
      showError && this.props.onErrorChange(this.props.name, T.translate('cl.validationErrors.badFormat'))
      return false
    } else if (this.props.minLength > 0 && value.length < this.props.minLength) {
      showError &&
        this.props.onErrorChange(
          this.props.name,
          T.translate('cl.validationErrors.tooShort', { length: this.props.minLength }),
        )
      return false
    } else if (this.props.maxLength > 0 && value.length > this.props.maxLength) {
      showError &&
        this.props.onErrorChange(
          this.props.name,
          T.translate('cl.validationErrors.tooLong', { length: this.props.maxLength }),
        )
      return false
    } else if (this.props.isPhone && !validate.validatePhone(value)) {
      showError && this.props.onErrorChange(this.props.name, T.translate('cl.validationErrors.badPhoneFormat'))
      return false
    } else if (this.props.isNumber && this.props.min && Number(value) < Number(this.props.min)) {
      let error = T.translate(`validationErrors.${this.props.name}.min`, { min: this.props.min })
      if (!error) {
        error = T.translate('cl.validationErrors.lessThanMin', { min: this.props.min })
      }
      showError && this.props.onErrorChange(this.props.name, error)
      return false
    } else if (
      this.props.isNumber &&
      this.props.range.start !== null &&
      this.props.range.end !== null &&
      (this.props.range.start > parseInt(value, 10) || this.props.range.end < parseInt(value, 10))
    ) {
      showError && this.props.onErrorChange(this.props.name, T.translate('cl.validationErrors.range', this.props.range))
      return false
    } else if (this.props.isNumber && !validate.isNumeric(value)) {
      showError && this.props.onErrorChange(this.props.name, T.translate('cl.validationErrors.numeric'))
      return false
    } else if (this.props.isInteger && !/^\d+$/.test(value)) {
      showError && this.props.onErrorChange(this.props.name, T.translate('cl.validationErrors.integer'))
      return false
    }

    this.props.onErrorChange(this.props.name, undefined) // eslint-disable-line
    return true
  }

  hasError = () => this.props.validationErrors[this.props.name] !== undefined // eslint-disable-line

  handleOnChange = (event) => {
    this.props.onChange(event.target.value)
  }

  handleOnBlur = (event) => {
    this.validate(true, event.target.value)
  }

  render() {
    const wrapperClasses = [
      'form-group d-flex flex-row flex-wrap flex-sm-row align-items-middle',
      { formError: this.hasError(), [this.props.wrapperClassName]: !!this.props.wrapperClassName },
    ]
    const inputClasses = [
      this.props.className,
      {
        'is-invalid': this.props.validationErrors && !!this.props.validationErrors[this.props.name],
      },
    ]
    return (
      <div className={classNames(wrapperClasses)}>
        <div className="col-10 col-sm-3 order-0 order-sm-0 pt-sm-2 mt-sm-1 text-sm-right">
          <label htmlFor={this.props.id}>
            {this.props.label} <span className="note"> {this.props.labelNote} </span>{' '}
          </label>
        </div>
        <div className="col-12 col-sm-8 order-3 order-sm-0 offset-sm-1 mt-2 mt-sm-0 pr-sm-0">
          <div className="d-inline-block w-100">
            <div className="form-group mb-0 d-flex flex-wrap align-items-center">
              <input
                className={classNames(inputClasses)}
                disabled={this.props.disabled}
                id={this.props.id}
                maxLength={this.props.maxLength}
                name={this.props.name}
                onBlur={this.handleOnBlur}
                onChange={this.handleOnChange}
                required={this.props.required}
                type={this.props.inputType}
                value={this.props.value}
              />
              {this.props.validationErrors[this.props.name] && (
                <div className="invalid-tooltip mt-0">
                  <span>{this.props.validationErrors[this.props.name]}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="" />
      </div>
    )
  }
}
